import { toast } from "react-hot-toast";
import React, { useEffect, useState } from "react";
import DivLoader from "components/divloader/DivLoader";
import { getClientTreasury, createClientTreasury } from "api/graphQL_clients";
import moment from "moment";
import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { getCurrencySymbol, currencyCodes } from "utils/currencyList";
import { Select as AntSelect } from "antd";

export interface TreasuryRowObj {
  client_id: any; gateway_id: any; amount: any;
  type: any; description: any; currency_code: any; created_at: any;
}
const columnHelper = createColumnHelper<TreasuryRowObj>();

const AddTreasury = ({ id, fetchClients, roleData }: { id: string; fetchClients: () => void; roleData?: any; }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddLoading, setIsAddLoading] = useState<boolean>(false);
  const [allTreasury, setAllTreasury] = useState([]);
  const [totalTreasury, setTotalTreasury] = useState<any>([]);
  const [amount, setAmount] = useState("");
  const [data, setData] = useState([]);
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState(null);
  const [pageSize] = useState<number>(50);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [amountError, setAmountError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [currencyError, setCurrencyError] = useState("");
  const [searchCurrency, setCurrencySearch] = useState(null);
  const [isAllData,setIsAllData] = useState(true);

  const fetchTreasury = async () => {
    setIsLoading(true);
    setIsAllData(false)
    getClientTreasury(id, searchCurrency)
      .then((d) => { setAllTreasury(d[0].treasury); setTotalTreasury(d[0].total_treasury); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Error fetching treasury"))
      .finally(() => setIsLoading(false));
  };

  const fetchAllTreasury = async () => {
    setIsLoading(true);
    setIsAllData(true)
    getClientTreasury(id, "")
      .then((d) => { setAllTreasury(d[0].treasury);})
      .catch((err) => toast.error(err?.response?.data?.message ?? "Error fetching treasury"))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { setData(allTreasury); }, [allTreasury]);
  useEffect(() => { if (searchCurrency)  { fetchTreasury();} else { fetchAllTreasury(); } }, [searchCurrency]);
  useEffect(() => {  fetchAllTreasury(); }, []);

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="at-th">Date</span>,
      cell: (info: any) => <span className="at-cell-date">{moment(info.getValue()).format("MMM DD, YYYY HH:mm")}</span>,
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <span className="at-th">Description</span>,
      cell: (info: any) => <span className="at-cell-desc">{info.getValue()}</span>,
    }),
    columnHelper.accessor("currency_code", {
      id: "currency_code",
      header: () => <span className="at-th">Currency</span>,
      cell: (info: any) => (
        <div className="at-amount-cell">
          <span className="at-amount">{info.row.original.currencyCode}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="at-th">Amount</span>,
      cell: (info: any) => (
        <div className="at-amount-cell">
          <span className={`at-amount ${info.row.original.type === "credit" ? "at-amount--credit" : "at-amount--debit"}`}>{Number(info?.getValue() ?? 0).toFixed(2)}</span>
          <span className={`at-type-badge${info.row.original.type === "credit" ? " at-type-badge--credit" : " at-type-badge--debit"}`}>
            {info.row.original.type === "credit" ? "+" : "−"}
          </span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({ data, columns, state: { pagination, sorting }, onPaginationChange: setPagination, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  const validateData = () => {
    let valid = true;
    if (!amount) { setAmountError("Required"); valid = false; } else setAmountError("");
    if (!description) { setDescriptionError("Required"); valid = false; } else setDescriptionError("");
    if (!currency) { setCurrencyError("Required"); valid = false; } else setCurrencyError("");
    return valid;
  };

  const handleAdd = () => {
    if (!validateData()) return;
    setIsAddLoading(true);
    createClientTreasury(id, amount, description, currency)
      .then((d) => {
        if (d[0].status === "Error") toast.error(d[0].message);
        else {
          toast.success(d[0].message);
          fetchAllTreasury(); setIsAllData(false); setTotalTreasury([]); setAmount(""); setDescription(""); setCurrency(null);
          setAmountError(""); setDescriptionError(""); setCurrencyError(""); setCurrencySearch(null);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Error"))
      .finally(() => setIsAddLoading(false));
  };

  const currencyOptions = currencyCodes.map((code: string) => {
    const d = getCurrencySymbol(code);
    return { value: code, label: `${code}: ${d?.title?.toUpperCase()}` };
  });

  return (
    <div className="at-root">
      <div className="at-page-header">
        <div className="at-header-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div>
          <h3 className="at-title">Client Treasury</h3>
          <p className="at-subtitle">Manage credit and debit entries for this client</p>
        </div>
      </div>

      <div className="at-body">
        {roleData?.[0]?.client?.value?.add_client_treasury && (
          <div className="at-add-card">
            <div className="at-add-card-header">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              Add New Entry
            </div>
            <div className="at-add-form">
              <div className="at-field">
                <label className="at-label">Amount <span className="at-req">*</span></label>
                <input type="text" className="at-input" placeholder="Enter amount" value={amount}
                  onChange={(e) => { const v = e.target.value; if (/^\d*\.?\d*$/.test(v)) { setAmount(v); if (!v) setAmountError("Required"); else setAmountError(""); } }} />
                {amountError && <p className="at-err">{amountError}</p>}
              </div>
              <div className="at-field">
                <label className="at-label">Description <span className="at-req">*</span></label>
                <input type="text" className="at-input" placeholder="Enter description" value={description}
                  onChange={(e) => { setDescription(e.target.value); if (!e.target.value) setDescriptionError("Required"); else setDescriptionError(""); }} />
                {descriptionError && <p className="at-err">{descriptionError}</p>}
              </div>
              <div className="at-field">
                <label className="at-label">Currency <span className="at-req">*</span></label>
                <AntSelect showSearch placeholder="Select currency"
                  filterOption={(input, option) => (option?.label ?? "").includes(input?.toUpperCase())}
                  className="at-ant-select-form" options={currencyOptions}
                  onChange={(e) => { setCurrency(e); if (!e) setCurrencyError("Required"); else setCurrencyError(""); }}
                  value={currency} notFoundContent="No options found" />
                {currencyError && <p className="at-err">{currencyError}</p>}
              </div>
              <div className="at-field at-field--btn">
                <button className="at-add-btn" onClick={handleAdd} disabled={!!isAddLoading}>
                  {isAddLoading ? <span className="at-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Add Entry</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="at-fetch-card">
          <div className="at-fetch-row">
            <div className="at-fetch-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <div className="at-fetch-info">
              <label className="at-fetch-label">Filter by Currency</label>
              <div className="at-filter-row">
                <AntSelect
                  allowClear
                  showSearch
                  placeholder="Select currency to view treasury"
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input?.toUpperCase())
                  }
                  className="at-ant-select at-ant-select--wide"
                  options={currencyOptions}
                  onChange={(e) => setCurrencySearch(e)}
                  value={searchCurrency}
                  notFoundContent="No options found"
                />

                {searchCurrency && (
                  <button
                    className="at-clear-btn"
                    onClick={() => setCurrencySearch(null)}
                  >
                    Clear Currency
                  </button>
                )}
              </div>
              {/* <AntSelect allowClear showSearch placeholder="Select currency to view treasury"
                filterOption={(input, option) => (option?.label ?? "").includes(input?.toUpperCase())}
                className="at-ant-select at-ant-select--wide"
                options={currencyOptions}
                onChange={(e) => setCurrencySearch(e)}
                value={searchCurrency} notFoundContent="No options found"/> */}
            </div>
          </div>
        </div>

        <div className="at-table-outer">
          <div className="at-table-card">
            {isLoading ? (
              <div className="at-loading"><div className="at-spinner-lg" /><p className="at-loading-txt">Loading treasury data…</p></div>
            ) : (
              <table className="at-table">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((h) => (
                        <th key={h.id} onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined} className="at-thead-th">
                          <div className="at-th-inner">
                            {flexRender(h.column.columnDef.header, h.getContext())}
                            {h.column.getCanSort() && (
                              <span className="at-sort-icon">
                                {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length > 0 ? (
                    <>
                      {table.getRowModel().rows.map((row, ri) => (
                        <tr key={row.id} className={`at-tbody-tr${ri % 2 !== 0 ? " at-tbody-tr--alt" : ""}`}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="at-tbody-td">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))}
                      {!isAllData && (
                        <tr className="at-total-row">
                          <td colSpan={3} className="at-total-label">Total Treasury</td>
                          <td className="at-total-value">{Number(totalTreasury).toFixed(2)} {searchCurrency}</td>
                        </tr>
                      )}
                    </>
                  ) : (
                    <tr><td colSpan={3}>
                      <div className="at-empty">
                        <div className="at-empty-icon"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div>
                        <p className="at-empty-title">{searchCurrency ? "No treasury records found" : "Select a currency to view treasury"}</p>
                        <p className="at-empty-sub">{searchCurrency ? "No entries exist for this currency" : "Use the filter above to load treasury data"}</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .at-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }
        .at-page-header { display:flex; align-items:flex-start; gap:14px; }
        .at-header-icon { width:44px; height:44px; border-radius:12px; background:linear-gradient(135deg,#fffbeb,#fef3c7); border:1.5px solid #fde68a; display:flex; align-items:center; justify-content:center; color:#d97706; flex-shrink:0; }
        .at-title { font-size:16px; font-weight:700; color:#0f172a; margin-bottom:3px; line-height:22px; }
        .at-subtitle { font-size:13px; color:#64748b; font-weight:500; }
        .at-body { display:flex; flex-direction:column; gap:16px; }

        /* Add card */
        .at-add-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:16px; overflow:hidden; }
        .at-add-card-header { display:flex; align-items:center; gap:8px; padding:14px 20px; background:#fafbfc; border-bottom:1px solid #f1f5f9; font-size:13px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:.4px; }
        .at-add-form { display:grid; grid-template-columns:1fr 1fr 1fr auto; gap:16px; padding:18px 20px; align-items:end; }
        .at-field { display:flex; flex-direction:column; gap:6px; }
        .at-field--btn { justify-content:flex-end; }
        .at-label { font-size:12px; font-weight:600; color:#374151; }
        .at-req { color:#dc2626; }
        .at-input { padding:11px 13px; background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:10px; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; color:#0f172a; outline:none; transition:border-color .18s,box-shadow .18s; width:100%; box-sizing:border-box; }
        .at-input:focus { border-color:#2563eb; background:#fff; box-shadow:0 0 0 3px rgba(37,99,235,.1); }
        .at-err { font-size:11.5px; color:#dc2626; font-weight:500; }
        .at-ant-select { width:100% !important; font-family:'Plus Jakarta Sans',sans-serif !important; }
        .at-ant-select-form { width:100% !important; font-family:'Plus Jakarta Sans',sans-serif !important; height:44px; }
        .at-add-btn { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:11px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 3px 12px rgba(37,99,235,.3); transition:all .15s; white-space:nowrap; }
        .at-add-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(37,99,235,.38); }
        .at-add-btn:disabled { opacity:.6; cursor:not-allowed; transform:none; }

        /* ── Fetch card — THE FIX ── */
        .at-fetch-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:14px; padding:18px 20px; }
        .at-fetch-row { display:flex; align-items:flex-end; gap:14px; flex-wrap:wrap; }
        .at-fetch-icon { width:40px; height:40px; border-radius:10px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .at-fetch-info {
          flex: 1;
          min-width: 0;           /* KEY: allows shrinking on mobile */
          display:flex; flex-direction:column; gap:6px;
        }
        .at-fetch-label { font-size:12px; font-weight:600; color:#374151; }
        /* Remove the fixed min-width that was forcing overflow */
        .at-ant-select--wide {
          width: 100% !important;
          min-width: 0 !important;  /* was 260px — caused overflow on mobile */
        }

        /* Table outer wrapper for horizontal scroll */
        .at-table-outer { overflow-x:auto; -webkit-overflow-scrolling:touch; border-radius:16px; }
        .at-table-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:16px; overflow:hidden; min-width:400px; }
        .at-table { width:100%; border-collapse:collapse; }
        .at-thead-th { padding:12px 16px 10px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; cursor:pointer; white-space:nowrap; user-select:none; }
        .at-th-inner { display:flex; align-items:center; gap:5px; }
        .at-th { font-size:11.5px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:.5px; }
        .at-sort-icon { color:#94a3b8; display:flex; align-items:center; }
        .at-tbody-tr { transition:background .12s; }
        .at-tbody-tr:hover { background:#f8fafc; }
        .at-tbody-tr--alt { background:#fafbfc; }
        .at-tbody-td { padding:12px 16px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
        .at-cell-date { font-size:13px; font-weight:600; color:#374151; white-space:nowrap; }
        .at-cell-desc { font-size:13px; color:#475569; font-weight:500; }
        .at-amount-cell { display:flex; align-items:center; gap:8px; }
        .at-amount { font-size:13.5px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .at-type-badge {
          font-size: 13px;
          font-weight: 800;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .at-type-badge--credit {
          color: #16a34a;
        }

        .at-type-badge--debit {
          color: #751616;
        }

        .at-amount--credit {
          color: #16a34a;
          font-weight: 700;
        }

        .at-amount--debit {
          color: #751616;
          font-weight: 700;
        }
        .at-filter-row {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
        }

        .at-clear-btn {
          height: 32px;
          padding: 0 20px;
          border: 1px solid #fecaca;
          background: #fff7f7;
          color: #b91c1c;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .at-clear-btn:hover {
          background: #fee2e2;
        }

        @media (max-width: 560px) {
          .at-filter-row {
            flex-direction: column;
            align-items: stretch;
          }

          .at-clear-btn {
            width: 100%;
          }
        }
        .at-total-row { background:#f8fafc; border-top:2px solid #e2e8f0; }
        .at-total-label { padding:13px 16px; font-size:13px; font-weight:700; color:#374151; text-align:right; }
        .at-total-value { padding:13px 16px; font-size:15px; font-weight:800; color:#1d4ed8; white-space:nowrap; }
        .at-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px; gap:12px; }
        .at-spinner { display:inline-block; width:16px; height:16px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:atSpin .65s linear infinite; }
        .at-spinner-lg { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:atSpin .7s linear infinite; }
        @keyframes atSpin { to { transform:rotate(360deg); } }
        .at-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }
        .at-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; gap:10px; }
        .at-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .at-empty-title { font-size:13.5px; font-weight:700; color:#64748b; }
        .at-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        /* ══ RESPONSIVE ══ */
        @media (max-width: 800px) { .at-add-form { grid-template-columns:1fr 1fr; } }

        @media (max-width: 560px) {
          .at-fetch-card { padding:14px 16px; }
          .at-fetch-row { gap:10px; }
          .at-fetch-icon { width:34px; height:34px; border-radius:8px; }
          .at-fetch-icon svg { width:14px; height:14px; }
          .at-fetch-label { font-size:11.5px; }
        }

        @media (max-width: 500px) {
          .at-add-form { grid-template-columns:1fr; }
        }

        @media (max-width: 380px) {
          .at-fetch-card { padding:12px 14px; }
          .at-fetch-row { gap:8px; }
          .at-add-form { gap:12px; padding:14px 16px; }
        }
      `}</style>
    </div>
  );
};

export default AddTreasury;